import { Icon } from "@blockdaemon-internal/design-system";
import {
  getSupportedLocales,
  PLATFORM_LOCALE_MENU_LABELS,
  type PlatformLocale,
  type WidgetProductId,
} from "@blockdaemon/widget-metadata";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Badge, Box, Group, Menu, UnstyledButton } from "@mantine/core";
import { ChevronDown, Languages } from "lucide-react";
import { useMemo } from "react";

import { activateLocale, type SupportedLocale } from "./i18n/locale";

const LOCALE_FLAGS: Record<PlatformLocale, string> = {
  "en-US": "🇺🇸",
  "ko-KR": "🇰🇷",
  "ja-JP": "🇯🇵",
};

const LANGUAGE_OPTIONS: Record<
  PlatformLocale,
  { value: PlatformLocale; label: string; flag: string }
> = {
  "en-US": {
    value: "en-US",
    label: PLATFORM_LOCALE_MENU_LABELS["en-US"],
    flag: LOCALE_FLAGS["en-US"],
  },
  "ko-KR": {
    value: "ko-KR",
    label: PLATFORM_LOCALE_MENU_LABELS["ko-KR"],
    flag: LOCALE_FLAGS["ko-KR"],
  },
  "ja-JP": {
    value: "ja-JP",
    label: PLATFORM_LOCALE_MENU_LABELS["ja-JP"],
    flag: LOCALE_FLAGS["ja-JP"],
  },
};

export function LanguageSwitcher({ productId }: { productId: WidgetProductId }) {
  const { i18n } = useLingui();
  const currentLocale = i18n.locale;
  const languageOptions = useMemo(
    () => getSupportedLocales(productId).map((locale) => LANGUAGE_OPTIONS[locale]),
    [productId],
  );

  const handleLanguageChange = (newLocale: SupportedLocale) => {
    activateLocale(newLocale);
  };

  return (
    <Menu width={180} offset={26} position="bottom-end" withinPortal>
      <Menu.Target>
        <UnstyledButton aria-label={t`Change language`}>
          <Group gap="0.25rem" wrap="nowrap">
            <Box component={Icon} icon={Languages} size="1.4rem" color="gray.0" />
            <Box component={Icon} icon={ChevronDown} size="1.4rem" color="gray.0" />
          </Group>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        {languageOptions.map((option) => {
          const isActive = option.value === currentLocale;
          return (
            <Menu.Item
              key={option.value}
              onClick={() => handleLanguageChange(option.value as SupportedLocale)}
              leftSection={<span>{option.flag}</span>}
              style={{
                background: isActive
                  ? "light-dark(var(--mantine-color-brand-5), var(--mantine-color-gray-9))"
                  : undefined,
              }}
            >
              <Group justify="space-between" wrap="nowrap" gap="xs">
                <Box component="span">{option.label}</Box>
                <Badge size="sm" variant="light">
                  {option.value}
                </Badge>
              </Group>
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}
